//#region [IMPORT]

// === THIRD-PARTY ===
import React, { useEffect, useRef, useState, } from 'react';
import dayjs from 'dayjs';

// ### MUI / STYLES ###
import { useTheme } from '@mui/material/styles';
import {
  MoreHoriz,
  Edit,
  Delete,
} from '@mui/icons-material';

// === COMPONENTS ===
import { useFile } from '../FileContext/FileContext';
import { NewComment } from './components/NewComment/NewComment';
import Styled from 'components/Styled/Styled';
import { useUserDetails } from 'components/hooks/useUserDetails/index';
import { Spacer } from 'components/Spacer';
import { CommentEdit } from './components/CommentEdit/CommentEdit';

// === API ===
import { on, removeListener, sendMessageData } from 'api/prokey/prokey.websocket';

//#endregion

//-----------------------------------------------------------------
export function FileComments( props ) {
  //-----------------------------------------------------------------
  //#region [VALUES]

  const { id } = props;

  const theme = useTheme();

  const { file_id, is_loading } = useFile();
  
  const [ thread, setThread ] = useState([]);

  //#endregion

  //#region [STYLES]

  const scrollbar_thin = {
    // scrollbarColor : `${theme.palette.primary.main} ${theme.palette.background.secondary[theme.palette.mode]}`,
    scrollbarWidth : 'thin'
  }

  //#endregion
    
  //#region [EFFECT]
  
  useEffect( () => {
    if( is_loading ) return;

    const THREAD = 'FILE_DOCUMENT_COMMENTS';
    
    //-----------------------------------------------------------------
    function handleThread( data ) {
      //-----------------------------------------------------------------
      setThread( data );
    }

    on( THREAD, handleThread );

    sendMessageData( 'FILE_REQUEST_DOCUMENT_COMMENTS', { file_id } );

    return () => {
      removeListener( THREAD, handleThread );
    }
  }, [file_id, is_loading] )

  //#endregion

  //#region [RENDER]

  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    return (
      <Styled styles={[{height:'calc( 100% )'},{overflow:'auto'},scrollbar_thin,{padding: '8px' }]} >
        {thread.comments?.map( com_id => <Comment key={com_id} id={com_id} file_id={file_id} document_id={thread.document_id} /> )||[]}
        <Styled key='spacer_1' styles={[{height: '2rem'}]} />
        <NewComment key='new_comment' file_id={file_id} />
      </Styled>
    )
  }

  //#endregion

  return display();
}

function Comment( props ) {

  //#region [VALUES]

  const { id, file_id, document_id } = props;

  const theme = useTheme();

  const user_details = useUserDetails();

  const ref_menu = useRef();

  const [ comment, setComment ] = useState([]);
  const [ is_edit, setIsEdit ] = useState( false );
  const [ is_open_menu_main, setIsOpenMenuMain ] = useState( false );

  //#endregion
    
  //#region [EFFECT]
  
  useEffect( () => {
    const COMMENT = 'FILE_COMMENT';
    
    //-----------------------------------------------------------------
    function handleThread( data ) {
      //-----------------------------------------------------------------
      if( id !== data.id ) return;
      setComment( data );
    }

    on( COMMENT, handleThread );

    sendMessageData( 'FILE_REQUEST_COMMENT', { file_id, document_id, comment_id: id } );

    return () => {
      removeListener( COMMENT, handleThread );
    }
  }, [] )
    
  useEffect(() => {
    if(is_open_menu_main) document.addEventListener   ( 'click', handleClickOutside, true );
    else                  document.removeEventListener( 'click', handleClickOutside, true );

    return () => {
      if(is_open_menu_main) document.removeEventListener('click', handleClickOutside, true);
    };
  }, [is_open_menu_main]);

  //#endregion
  
  //#region [EVENTS]

  //-----------------------------------------------------------------
  function onClick_Main() {
    //-----------------------------------------------------------------
    try {
      setIsOpenMenuMain( !is_open_menu_main );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_Edit() {
    //-----------------------------------------------------------------
    try {
      setIsEdit( true );
      setIsOpenMenuMain( false );
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClick_Remove() {
    //-----------------------------------------------------------------
    try {
      setIsOpenMenuMain( false );
      sendMessageData( 'FILE_COMMENT_REMOVE', {file_id, document_id, comment_id: id} )
    } catch (error) {
      console.warn(error);
    }
  }

  //-----------------------------------------------------------------
  function onClose_Edit() {
    //-----------------------------------------------------------------
    setIsEdit( false );
  }

  //-----------------------------------------------------------------
  function handleClickOutside(event) {
    //-----------------------------------------------------------------
    if (ref_menu.current && !ref_menu.current.contains(event.target)) {
        setIsOpenMenuMain(false);
    }
  }

  //#endregion

  //#region [RENDER]

  const border_pd = {border: '2px solid ' + theme.palette.primary.main, borderRadius: '5px'};
  const border_deleted = {border: '2px solid grey', borderRadius: '5px'};
  //-----------------------------------------------------------------
  function display() {
    //-----------------------------------------------------------------
    if( comment.removed_at != undefined ) {
      return (
        <Styled styles={[{height:'fit-content'},{paddingRight:'1rem'}]} disabled >
          <Styled styles={[{marginLeft: '1rem'},border_deleted,{width: 'fit-content', maxWidth:'100%'},{padding: '5px'},{whiteSpace:'pre-wrap'},{overflowWrap: 'break-word'}, {cursor:'text'}]}>
            Comment removed by user.
          </Styled>
          <Styled styles={[{height:'12px'}]} />
        </Styled>
      )
    }

    return (
      <Styled styles={[{height:'fit-content'},{paddingRight:'1rem'}]} >
        <Styled styles={[{display: 'flex'}]}>
          <Styled styles={[{marginLeft:'1rem'},{height: '2rem'}, {fontSize:'18px'},{fontWeight: 'bold'}]}>
            {comment.user_name}
          </Styled>
          {displayMenuMain()}
        </Styled>
        <Styled styles={[{marginLeft:'1rem'},{height: '2rem'}, {fontSize:'12px'}]}>
          {dayjs( comment.updated_at ).format('MMMM DD, YYYY [at] hh:mm A')}
          {comment.updated_at !== comment.created_at ? ' - Edited':''}
        </Styled>
        {displayText()}
        <Styled styles={[{height:'12px'}]} />
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function displayMenuMain() {
    //-----------------------------------------------------------------
    if( user_details.Id !== comment.user_id || is_edit ) {
      return;
    }
    return (
      <Styled ref={ref_menu}>
        <Styled styles={[ {marginLeft: 'auto'},{width:'fit-content'}, {cursor:'pointer'}, {anchorName: `--main_menu-${comment.id}`} ]} onClick={onClick_Main}>
          <MoreHoriz />
        </Styled>
        {displayMenuMainOptions()}
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function displayMenuMainOptions() {
    //-----------------------------------------------------------------
    if( !is_open_menu_main ) {
      return;
    }
    return (
      <Styled styles={[ { position: 'absolute', positionAnchor: `--main_menu-${comment.id}`, positionArea: 'bottom center', marginRight: 80 }, { width: '100px', height: 'fit-content'}, {background: theme.palette.background.secondary[theme.palette.mode]} ]}>
        <Styled styles={[{ display: 'flex' }, { width: '100px', height: '30px', padding: 4}, {cursor:'pointer'}]} onClick={onClick_Edit}>
          <Edit /> <Spacer size={4} /> Edit
        </Styled>
        <Styled styles={[{ display: 'flex' }, { width: '100px', height: '30px', padding: 4}, {cursor:'pointer'}]} onClick={onClick_Remove}>
          <Delete /> <Spacer size={4} /> Remove
        </Styled>
      </Styled>
    )
  }

  //-----------------------------------------------------------------
  function displayText() {
    //-----------------------------------------------------------------
    if( is_edit ) {
      return <CommentEdit file_id={file_id} comment_id={id} comment={comment.text} handleClose={onClose_Edit}/>;
    }

    return (
      <Styled styles={[{marginLeft: '1rem'},border_pd,{width: 'fit-content', maxWidth:'100%'},{padding: '5px'},{whiteSpace:'pre-wrap'},{overflowWrap: 'break-word'}, {cursor:'text'}]}>
        {comment.text}
      </Styled>
    )
  }

  //#endregion

  return display();
}